'use client';

import { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { conversationClient, conversationTagsClient, type ConversationTag } from '@/lib/gupshup-client';

/**
 * Compact tag editor for an open conversation: shows the applied status tags and lets the operator
 * add/remove them. Self-contained — fetches the brand catalog + the conversation's current tags.
 */
export function ConversationTagsEditor({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation('conversations');
  const [catalog, setCatalog] = useState<ConversationTag[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    conversationTagsClient.list().then(setCatalog).catch(() => setCatalog([]));
  }, []);

  useEffect(() => {
    conversationClient.conversations
      .get(conversationId)
      .then((r) => setSelected(r.data.tagIds ?? []))
      .catch(() => setSelected([]));
  }, [conversationId]);

  const toggle = async (tagId: string) => {
    const previous = selected;
    const next = selected.includes(tagId) ? selected.filter((id) => id !== tagId) : [...selected, tagId];
    setSelected(next);
    setSaving(true);
    try {
      const res = await conversationClient.conversations.setTags(conversationId, next);
      setSelected(res.tagIds);
    } catch {
      setSelected(previous);
    } finally {
      setSaving(false);
    }
  };

  const byId = new Map(catalog.map((t) => [t.id, t]));
  const appliedTags = selected.map((id) => byId.get(id)).filter((t): t is ConversationTag => Boolean(t));

  return (
    <div className="relative flex items-center gap-1.5 flex-wrap">
      {appliedTags.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium"
          style={{ backgroundColor: `${tag.color}22`, color: tag.color }}
        >
          {tag.name}
        </span>
      ))}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('tags.editAria')}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-chat-border text-chat-text-muted hover:bg-chat-panel"
      >
        <Tag className="h-3 w-3" />
        {t('tags.label')}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-20 w-56 max-h-72 overflow-auto rounded-lg border border-chat-border bg-chat-elevated shadow-lg p-1">
            {catalog.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-chat-text-muted">{t('tags.empty')}</p>
            ) : (
              catalog.map((tag) => (
                <label
                  key={tag.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-chat-panel cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(tag.id)}
                    onChange={() => toggle(tag.id)}
                    disabled={saving}
                  />
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                  <span className="truncate text-chat-text">{tag.name}</span>
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
